import { AsYouType, getCountryCallingCode, parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js/min';
import { createCountryPicker } from './country-picker.js';

export interface PhoneField {
  element: HTMLElement;
  setValue(value: string): void;
  getValue(): string;
  dispose(): void;
}

// Extensions remain editable text, including partially typed or nonnumeric ones.
// AsYouType only sees the number so it cannot discard an extension's contents.
function parts(value: string): { number: string; extension: string } {
  const match = /\s*(?:ext(?:ension)?\.?|x|#)/i.exec(value);
  return match && /\d/.test(value.slice(0, match.index))
    ? { number: value.slice(0, match.index), extension: value.slice(match.index) }
    : { number: value, extension: '' };
}

const digits = (value: string): string => value.replace(/\D/g, '');
const tokens = (value: string): number => (value.match(/[\da-z+#]/gi) ?? []).length;

function caretAfterTokens(value: string, count: number): number {
  if (!count) return 0;
  for (let index = 0; index < value.length; index++) {
    if (/[\da-z+#]/i.test(value[index]) && --count === 0) return index + 1;
  }
  return value.length;
}

/** A presentation controller; requiredness and domain validation stay with the form. */
export function createPhoneField(input: HTMLInputElement, options: { countryLabel: string }): PhoneField {
  const element = document.createElement('div');
  element.className = 'phone-input';
  const picker = createCountryPicker({ label: options.countryLabel, onChange: onCountryChange });
  input.classList.add('phone-number');
  input.type = 'tel';
  input.inputMode = 'tel';
  input.autocomplete = 'tel';
  // Historical ten-digit masks must not constrain international input or extensions.
  input.removeAttribute('maxlength');
  input.before(element);
  element.append(picker.element, input);

  let country: CountryCode | undefined = 'US';
  let original = input.value;
  let edited = false;
  let composing = false;
  let unresolvedCallingCode = '+';

  function infer(number: string): void {
    if (!number.trimStart().startsWith('+')) return;
    const formatter = new AsYouType();
    formatter.input(number.trim());
    country = formatter.getCountry();
    unresolvedCallingCode = `+${formatter.getCallingCode() ?? digits(number)}`;
  }

  function format(): void {
    const value = input.value;
    const start = input.selectionStart ?? value.length;
    const end = input.selectionEnd ?? start;
    const direction = input.selectionDirection ?? 'none';
    const { number, extension } = parts(value);
    infer(number);
    picker.setCountry(country, unresolvedCallingCode);
    // Unexpected text stays visible and collectable instead of being sanitized away.
    if (!/^\+?[\d\s()./\-]*$/.test(number.trim())) return;
    const formatter = new AsYouType(country);
    let formatted = formatter.input(number.trim());
    if (digits(formatted) !== digits(number)) return;
    let removedTokens = 0;
    const parsed = formatter.getNumber();
    const international = number.trimStart().startsWith('+');
    const numberDigits = digits(number);
    // Wait for a complete, assigned number before moving its prefix into the
    // picker. In particular, a leading 1 must survive incremental US typing.
    const callingCode = international ? formatter.getCallingCode() : country && getCountryCallingCode(country);
    const hasCallingPrefix = international || (callingCode === '1' && numberDigits.length === 11 && numberDigits.startsWith('1'));
    if (country && parsed?.isPossible() && callingCode && hasCallingPrefix) {
      const nationalDigits = numberDigits.slice(callingCode.length);
      const national = new AsYouType(country).input(nationalDigits);
      // Keep trunk zeroes and significant national digits exactly as entered.
      // Some countries only format a trunk-free number in international mode.
      const candidate = digits(national) === nationalDigits ? national : nationalDigits;
      const reparsed = parsePhoneNumberFromString(candidate, { defaultCountry: country, extract: false });
      if (reparsed?.number === parsed.number) {
        formatted = candidate;
        removedTokens = callingCode.length + (international ? 1 : 0);
      }
    }
    if (country && parsed?.isPossible() && formatted === digits(formatted)) {
      // Preserve useful grouping for trunk-free GB/AU input on later edits too.
      const grouped = parsed.formatInternational().slice(parsed.countryCallingCode.length + 1).trimStart();
      if (digits(grouped) === formatted) formatted = grouped;
    }
    input.value = formatted + extension;
    const nextStart = start === value.length ? input.value.length : caretAfterTokens(input.value, Math.max(0, tokens(value.slice(0, start)) - removedTokens));
    const nextEnd = end === value.length ? input.value.length : caretAfterTokens(input.value, Math.max(0, tokens(value.slice(0, end)) - removedTokens));
    input.setSelectionRange(nextStart, nextEnd, direction);
  }

  function onInput(event: Event): void {
    edited = true;
    if (!composing && !(event instanceof InputEvent && event.isComposing)) format();
  }

  function onBeforeInput(event: InputEvent): void {
    if (!event.cancelable || composing || (event.inputType !== 'deleteContentBackward' && event.inputType !== 'deleteContentForward')) return;
    const start = input.selectionStart;
    if (start === null || start !== input.selectionEnd) return;
    const { number } = parts(input.value);
    const backward = event.inputType === 'deleteContentBackward';
    let index = backward ? start - 1 : start;
    // Native deletion handles digits, selections, extensions and the leading +.
    if (index < 0 || index >= number.length || !/[\s()./\-]/.test(number[index])) return;
    while (index >= 0 && index < number.length && /[\s()./\-]/.test(number[index])) index += backward ? -1 : 1;
    if (index < 0 || index >= number.length || !/\d/.test(number[index])) return;
    event.preventDefault();
    input.value = input.value.slice(0, index) + input.value.slice(index + 1);
    const caret = backward ? index : start;
    input.setSelectionRange(caret, caret);
    input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: event.inputType }));
  }

  function onCountryChange(next: CountryCode): void {
    const { number, extension } = parts(input.value);
    let national = number;
    if (number.trimStart().startsWith('+')) {
      const formatter = new AsYouType();
      formatter.input(number.trim());
      const callingCode = formatter.getCallingCode();
      // Remove only the known old calling prefix; preserve every national digit.
      national = callingCode ? digits(number).slice(callingCode.length) : digits(number);
    }
    country = next;
    input.value = national + extension;
    input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertReplacementText' }));
  }

  const onCompositionStart = (): void => { composing = true; };
  const onCompositionEnd = (): void => { composing = false; format(); };
  input.addEventListener('input', onInput);
  input.addEventListener('beforeinput', onBeforeInput);
  input.addEventListener('compositionstart', onCompositionStart);
  input.addEventListener('compositionend', onCompositionEnd);

  function setValue(value: string): void {
    original = value;
    edited = false;
    country = 'US';
    input.value = value;
    format();
  }
  setValue(original);

  return {
    element,
    setValue,
    getValue(): string {
      if (!edited) return original;
      if (!input.value.trim()) return '';
      const { number, extension } = parts(input.value);
      const parsed = parsePhoneNumberFromString(number.trim(), { defaultCountry: country, extract: false });
      return parsed ? parsed.number + extension : input.value;
    },
    dispose(): void {
      input.removeEventListener('input', onInput);
      input.removeEventListener('beforeinput', onBeforeInput);
      input.removeEventListener('compositionstart', onCompositionStart);
      input.removeEventListener('compositionend', onCompositionEnd);
      picker.dispose();
    },
  };
}
